import type { ComponentType } from "react";
import type { SvgIconProps } from "@mui/material/SvgIcon";
import BedIcon from "@mui/icons-material/Bed";
import EmergencyIcon from "@mui/icons-material/Emergency";
import WaterDropIcon from "@mui/icons-material/WaterDrop";
import WcIcon from "@mui/icons-material/Wc";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type { RequestTypeItem } from "@/lib/elevenlabs/agent";

type IconComponent = ComponentType<SvgIconProps>;

/** Whitelist of DB IconKey values → MUI icons (avoid dynamic import of entire icons package). */
const ICON_MAP: Record<string, IconComponent> = {
  WaterDrop: WaterDropIcon,
  Wc: WcIcon,
  Bed: BedIcon,
  Emergency: EmergencyIcon,
};

function resolveIcon(iconKey: string | null | undefined): IconComponent {
  if (!iconKey) return HelpOutlineOutlinedIcon;
  return ICON_MAP[iconKey] ?? HelpOutlineOutlinedIcon;
}

/**
 * Figma 20183:57174 at 1.5× — a 48px glyph in a 13.5px-padded pill (so a
 * 75px circle), over a 13px SemiBold uppercase label, in a 150px column.
 * The teal is the icons' own fill colour from the exported SVGs.
 */
const GLYPH_PX = 48;
const GLYPH_PAD_PX = 13.5;
const BUTTON_WIDTH_PX = 150;

type Props = {
  items: RequestTypeItem[];
};

/**
 * On-screen hints for what the patient can ask. Driven by DB RequestType.IconKey (MUI names).
 */
export function RequestTypeHints({ items }: Props) {
  if (items.length === 0) return null;

  return (
    <Stack
      component="section"
      aria-label="Things you can ask for"
      sx={{
        width: "100%",
        gap: "12px",
        alignItems: "center",
        pointerEvents: "none",
      }}
    >
      <Typography
        sx={{
          width: "100%",
          fontFamily: "var(--font-sans)",
          fontWeight: 700,
          fontSize: "14px",
          lineHeight: "16.5px",
          letterSpacing: "0.55px",
          textTransform: "uppercase",
          textAlign: "center",
          color: "var(--neutrals-100, #c4c4c4)",
        }}
      >
        You can ask for
      </Typography>

      <Stack
        direction="row"
        useFlexGap
        sx={{
          width: "100%",
          gap: "8px",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {items.map((item) => {
          const Icon = resolveIcon(item.iconKey);
          return (
            <Stack
              key={item.code}
              sx={{
                width: `${BUTTON_WIDTH_PX}px`,
                gap: "12px",
                flexShrink: 0,
                alignItems: "center",
                justifyContent: "center",
                px: "16px",
                py: "12px",
                borderRadius: "14px",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  p: `${GLYPH_PAD_PX}px`,
                  borderRadius: "100px",
                  bgcolor: "rgba(59, 184, 193, 0.12)",
                  border: "1px solid rgba(59, 184, 193, 0.22)",
                }}
              >
                <Icon
                  sx={{
                    width: `${GLYPH_PX}px`,
                    height: `${GLYPH_PX}px`,
                    fontSize: `${GLYPH_PX}px`,
                    color: "var(--hint-accent, #3bb8c1)",
                  }}
                  aria-hidden
                />
              </Box>
              <Typography
                sx={{
                  fontFamily: "var(--font-sans)",
                  fontWeight: 600,
                  fontSize: "13px",
                  lineHeight: "19.5px",
                  textTransform: "uppercase",
                  textAlign: "center",
                  whiteSpace: "nowrap",
                  color: "rgba(255, 255, 255, 0.85)",
                }}
              >
                {item.title}
              </Typography>
            </Stack>
          );
        })}
      </Stack>
    </Stack>
  );
}
